/**
 * MyInvestFolio — User-facing label constants.
 *
 * This file is the active source of truth for user-facing labels tied
 * to the tier restructure (Continuity System as the entry tier,
 * Portfolio Clarity as the top tier). It exports:
 *   - TIER      : backend tier value → display label
 *   - SECTION   : section titles / breadcrumbs for the Continuity area
 *   - UPGRADE   : gating copy used by the LockedFeature wrapper
 *   - tierLabel : helper that translates backend tier → display label
 *
 * IMPORTANT: backend field names, database enum values, and API routes
 * are DELIBERATELY NOT renamed. Frontend translates at render time via
 * this file. See /app/memory/PORTFOLIO_CONTINUITY_RESTRUCTURE_SPEC.md.
 *
 * Tier-key migration note (do not remove without founder sign-off):
 *   The old top tier was internally called `premium_continuity` and is
 *   being migrated over to `premium_portfolio`. Both keys currently
 *   display as "Portfolio Clarity" so users see one product name
 *   throughout the migration. Once every `premium_continuity` record
 *   has been re-keyed to `premium_portfolio`, the older mapping can be
 *   removed from the TIER map below.
 *
 * When user-facing wording changes in the future, change ONLY this file.
 * Do not scatter user-facing strings across components.
 */

// ─────────────────────────────────────────────────────────────
// Product & tier names
// ─────────────────────────────────────────────────────────────
export const PRODUCT = {
  // Backend still stores "estate_vault" in enums and collections.
  CONTINUITY_FULL: 'Continuity System',
  CONTINUITY: 'Continuity System',
  CONTINUITY_SHORT: 'Continuity',
  PREMIUM: 'Portfolio Clarity',
};

export const TIER = {
  // Maps backend tier values to display labels. Backend DB values remain
  // untouched (`portfolio_continuity` / `premium_portfolio` / migrating
  // `premium_continuity`). The 2026-Q3 rename brought the user-facing
  // labels to "Continuity System" (entry tier) and "Portfolio Clarity"
  // (top tier). The `premium_continuity` key represents the older
  // internal name for the top tier and is being migrated to
  // `premium_portfolio` — both display as Portfolio Clarity in the
  // meantime so users see one product name throughout the migration.
  premium_continuity: 'Portfolio Clarity',
  premium_portfolio: 'Portfolio Clarity',
  portfolio_continuity: 'Continuity System',
};

// ─────────────────────────────────────────────────────────────
// Navigation labels
// ─────────────────────────────────────────────────────────────
export const NAV = {
  CONTINUITY: 'Continuity System',
  CONTINUITY_DOCUMENTS: 'Continuity Documents',
  TRUSTED_CONTACTS: 'Trusted Contacts',
  WALKTHROUGH_VIDEOS: 'Walkthrough Videos',
  ASSETS_SUMMARY: 'Assets Summary',
  INVESTMENTS: 'Investments',
  ADD_INVESTMENT: 'Add Investment',
  VIEW_INVESTMENTS: 'View Investments',
  ASSET_ALLOCATION: 'Asset Allocation',
  PORTFOLIO_PERFORMANCE: 'Portfolio Performance',
  TAX_DATA_PACK: 'Tax Data Pack',
  PORTFOLIO_INSIGHTS: 'Portfolio Insights',
  NET_WORTH: 'Net Worth',
};

// ─────────────────────────────────────────────────────────────
// Section titles & breadcrumbs
// ─────────────────────────────────────────────────────────────
export const SECTION = {
  CONTINUITY_DOCS_TITLE: 'Continuity Documents',
  CONTINUITY_DOCS_BREADCRUMB: 'Continuity › Trusted Contacts',
  CONTINUITY_WALKTHROUGH_TITLE: 'Continuity Walkthrough',
};

// ─────────────────────────────────────────────────────────────
// Upgrade / gating copy
// ─────────────────────────────────────────────────────────────
export const UPGRADE = {
  LOCKED_TOOLTIP_SUFFIX: 'Available on Portfolio Clarity. Learn more →',
  MODAL_TITLE: 'Upgrade to Portfolio Clarity',
  REQUEST_INVITATION_CTA: 'Request Invitation',
  QUALIFIED_INVITATION_CTA: 'Enter Invitation Code',
  ASSET_THRESHOLD_USD: 500000,
  PORTFOLIO_COMPLETENESS_PCT: 75,
};

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

/** Translate a backend tier value into its user-facing label. */
export const tierLabel = (backendTierValue) =>
  TIER[backendTierValue] || backendTierValue || '';
